import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  DollarSign, 
  Users, 
  FolderKanban, 
  Calendar, 
  UserCog, 
  FileText, 
  Settings,
  Shield,
  LogOut,
  Rocket,
  Menu,
  X,
  ListTodo,
  Inbox,
  QrCode,
  UserPlus,
  Mail,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sidebar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const response = await fetch(`${API}/messages/unread-count`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unread_count || 0);
        }
      } catch {
        // Silent fail - unread count is non-critical
      }
    };
    fetchUnread();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile && isOpen) {
      // Use a small delay to avoid cascading renders
      const timer = setTimeout(() => setIsOpen(false), 0);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle swipe to close
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    let startX = 0;
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX;
      if (startX - endX > 50) {
        setIsOpen(false);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, isOpen]);

  const isAdmin = user?.role === "owner" || user?.role === "co_ceo" || user?.role === "super_admin" || user?.role === "staff_admin";

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Mail, label: "Messages", path: "/messages", badge: unreadCount > 0 ? unreadCount : null, testId: "nav-messages" },
    { icon: ListTodo, label: "Tasks", path: "/tasks" },
    { icon: DollarSign, label: "Money", path: "/money" },
    { icon: FolderKanban, label: "Projects", path: "/projects" },
    { icon: Users, label: "Clients", path: "/clients" },
    { icon: Calendar, label: "Content", path: "/content" },
    { icon: UserCog, label: "Team", path: "/team" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: User, label: "My Profile", path: "/profile", testId: "nav-profile" },
    { icon: Settings, label: "Settings", path: "/settings" },
  ];

  // Add admin menu item if user is admin
  if (isAdmin) {
    menuItems.push({ icon: Shield, label: "User Management", path: "/admin/users" });
    menuItems.push({ icon: UserPlus, label: "Staff Approvals", path: "/admin/staff-approvals", testId: "nav-staff-approvals" });
    menuItems.push({ icon: Inbox, label: "Submissions", path: "/admin/submissions" });
    menuItems.push({ icon: QrCode, label: "QR Codes", path: "/admin/qr-codes" });
    menuItems.push({ icon: Rocket, label: "Promo Campaigns", path: "/admin/promo/campaigns" });
  }

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/staff/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/staff/login", { replace: true });
  };

  const getRoleBadge = (role) => {
    const roleLabels = {
      super_admin: "Super Admin",
      staff_admin: "Admin",
      owner: "CEO",
      co_ceo: "Co-CEO",
      treasurer: "CFO",
      secretary: "Admin",
      director: "Director",
      videographer: "Editor",
      artist: "Artist"
    };
    return roleLabels[role] || role;
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 bg-[#121214] border border-[#27272A] rounded-lg text-[#FAFAFA] hover:border-[#D4AF37] transition-colors"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar - Luminous Style */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"
        }`}
        style={{
          background: 'linear-gradient(180deg, rgba(15, 10, 5, 0.95) 0%, rgba(10, 5, 0, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(255, 215, 0, 0.2)',
          boxShadow: '0 0 40px rgba(255, 200, 100, 0.05)',
        }}
        data-testid="sidebar"
      >
        {/* Header with close button on mobile */}
        <div className="px-3 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.15)' }}>
          <div>
            <img
              src="/logo-reflection-trimmed.png"
              alt="DopeInHD"
              className="h-10 md:h-11 lg:h-12 w-auto max-w-full object-contain block"
              style={{ filter: 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.4))' }}
              data-testid="sidebar-logo"
            />
            <p className="text-xs mt-2" style={{ color: '#FFD700', letterSpacing: '0.15em', textShadow: '0 0 10px rgba(255, 215, 0, 0.3)' }}>COMMAND CENTER</p>
          </div>
          {isMobile && (
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-[#E5C558] hover:text-[#FFD700] transition-colors"
              data-testid="close-sidebar-btn"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-all duration-300 ${
                  isActive 
                    ? "text-[#FFD700]" 
                    : "text-[#E5C558] hover:text-[#FFD700]"
                }`}
                style={{
                  borderLeft: isActive ? '3px solid #FFD700' : '3px solid transparent',
                  background: isActive ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                  boxShadow: isActive ? 'inset 0 0 20px rgba(255, 215, 0, 0.05)' : 'none',
                }}
                data-testid={item.testId || `nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} />
                <span className="font-medium flex-1">{item.label}</span>
                {item.badge && (
                  <span 
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ 
                      background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                      color: '#0a0500',
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.4)',
                    }}
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Profile - Luminous Style */}
        <div className="p-4" style={{ borderTop: '1px solid rgba(255, 215, 0, 0.15)' }}>
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-10 h-10 rounded-full"
                style={{ border: '2px solid #FFD700', boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)' }}
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                style={{ 
                  background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                  color: '#0a0500',
                  boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)'
                }}
              >
                {user?.name?.charAt(0) || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#FFFFFF] truncate">{user?.name}</p>
              <span 
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ 
                  background: 'rgba(255, 215, 0, 0.2)',
                  color: '#FFD700',
                  border: '1px solid rgba(255, 215, 0, 0.3)'
                }}
              >
                {getRoleBadge(user?.role)}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-[#E5C558] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-all"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
